import { AudioCapture } from './AudioCapture';
import { WebSpeechSTTProvider } from './WebSpeechSTTProvider';
import { transcribeBlob, type TranscribeFn } from './backendTransport';
import { transcribeAudio } from '../../utils/transcribe';
import { diag } from '../diag';
import type {
  STTConfig,
  STTError,
  STTEventMap,
  STTProvider,
  STTResult,
} from './types';

/** Injectable dependencies (defaults wire up the real implementations). */
export interface NativeSTTProviderDeps {
  webSpeech?: STTProvider;
  createCapture?: () => AudioCapture;
  transcribe?: TranscribeFn;
}

/**
 * `native`-mode {@link STTProvider}: use the Web Speech API when it is supported,
 * otherwise fall back to the backend (GCP) capture + upload path.
 *
 * Fallback is at the **capability** level, not per-utterance. When Web Speech is
 * supported, nothing records audio for upload: there is no buffered audio to
 * re-send if Web Speech fails mid-turn, so a runtime failure surfaces as an
 * error and the user retries. (On iOS the recorder does hold a raw mic stream
 * open during the turn, which keeps the recognizer hearing; see `speech/mic.ts`.
 * An earlier parallel `MediaRecorder` buffer on a default-constraints stream
 * starved the recognizer, which is why none is kept.) Only when Web Speech is
 * unsupported do we record the turn's stream and upload it to the backend
 * (equivalent to {@link BackendSTTProvider}).
 */
export class NativeSTTProvider implements STTProvider {
  private readonly webSpeech: STTProvider;
  private readonly createCapture: () => AudioCapture;
  private readonly transcribe: TranscribeFn;

  private capture: AudioCapture | null = null;
  private config: STTConfig | null = null;
  private usingWebSpeech = false;
  /** Guard so each turn emits exactly one final result or error. */
  private handled = false;
  /**
   * Set by dispose(). Guards uploadBuffer() against emitting after component
   * unmount when the transcribeBlob network round-trip outlives the component.
   */
  private disposed = false;

  private resultHandlers = new Set<STTEventMap['result']>();
  private errorHandlers = new Set<STTEventMap['error']>();

  constructor(deps: NativeSTTProviderDeps = {}) {
    this.webSpeech = deps.webSpeech ?? new WebSpeechSTTProvider();
    this.createCapture =
      deps.createCapture ?? ((): AudioCapture => new AudioCapture());
    this.transcribe = deps.transcribe ?? transcribeAudio;

    this.capturesAudio = !this.webSpeech.isSupported();

    this.webSpeech.addEventListener('result', (r) => this.onWebSpeechResult(r));
    this.webSpeech.addEventListener('error', (e) => this.onWebSpeechError(e));
  }

  /**
   * Fixed at construction: Web Speech captures the mic itself; the fallback
   * records from the turn's stream.
   */
  readonly capturesAudio: boolean;

  isSupported(): boolean {
    return this.webSpeech.isSupported();
  }

  async startListening(config: STTConfig): Promise<void> {
    this.config = config;
    this.handled = false;
    this.usingWebSpeech = this.webSpeech.isSupported();
    diag(
      'NativeSTTProvider.startListening usingWebSpeech=',
      this.usingWebSpeech
    );

    if (this.usingWebSpeech) {
      // Web Speech captures the mic itself; no upload buffer is recorded.
      await this.webSpeech.startListening(config);
      return;
    }

    // Web Speech unsupported: this capture IS the backend upload source.
    this.capture = this.createCapture();
    try {
      await this.capture.start({ stream: config.stream });
    } catch (err) {
      this.capture = null;
      this.emitError(this.captureError(err));
    }
  }

  stopListening(): void {
    diag(
      'NativeSTTProvider.stopListening usingWebSpeech=',
      this.usingWebSpeech
    );
    if (this.usingWebSpeech) {
      // Finalizes Web Speech, which emits the final result or an error.
      this.webSpeech.stopListening();
      return;
    }
    // Backend-only path: stopping ends capture and triggers the upload.
    void this.uploadBuffer();
  }

  addEventListener<E extends keyof STTEventMap>(
    event: E,
    handler: STTEventMap[E]
  ): void {
    if (event === 'result') {
      this.resultHandlers.add(handler as STTEventMap['result']);
    } else {
      this.errorHandlers.add(handler as STTEventMap['error']);
    }
  }

  removeEventListener<E extends keyof STTEventMap>(
    event: E,
    handler: STTEventMap[E]
  ): void {
    if (event === 'result') {
      this.resultHandlers.delete(handler as STTEventMap['result']);
    } else {
      this.errorHandlers.delete(handler as STTEventMap['error']);
    }
  }

  dispose(): void {
    diag('NativeSTTProvider.dispose');
    this.disposed = true;
    this.webSpeech.dispose();
    this.capture?.dispose();
    this.capture = null;
    this.config = null;
    this.resultHandlers.clear();
    this.errorHandlers.clear();
  }

  private onWebSpeechResult(result: STTResult): void {
    if (!this.usingWebSpeech) return;
    if (!result.isFinal) {
      // Interim result: forward for live display; the turn isn't finished yet.
      this.emitResult(result);
      return;
    }
    if (this.handled) return;
    this.handled = true;
    this.emitResult(result);
  }

  private onWebSpeechError(error: STTError): void {
    if (!this.usingWebSpeech) return;
    if (this.handled) return;
    this.handled = true;
    // No buffered audio on Safari, so every native runtime error surfaces to the
    // user for a retry — there is no seamless GCP fallback for the same utterance.
    this.emitError(error);
  }

  private async uploadBuffer(): Promise<void> {
    const capture = this.capture;
    const config = this.config;
    if (!capture || !config || this.handled) return;
    this.handled = true;
    this.capture = null;
    const blob = await capture.stop();
    capture.dispose();
    // Guard: dispose() may have been called while capture.stop() awaited
    // (component unmount during upload). Bail out to avoid emitting into
    // a cleared handler set and silently dropping the transcript.
    if (this.disposed) return;
    const outcome = await transcribeBlob(blob, config, this.transcribe);
    if (this.disposed) return;
    if (outcome.ok) {
      this.emitResult(outcome.result);
    } else {
      this.emitError(outcome.error);
    }
  }

  private captureError(err: unknown): STTError {
    const denied =
      err instanceof Error &&
      (err.name === 'NotAllowedError' || err.name === 'SecurityError');
    return {
      code: denied ? 'not-allowed' : 'unknown',
      message: err instanceof Error ? err.message : String(err),
    };
  }

  private emitResult(result: STTResult): void {
    this.resultHandlers.forEach((handler) => handler(result));
  }

  private emitError(error: STTError): void {
    this.errorHandlers.forEach((handler) => handler(error));
  }
}
